// ─────────────────────────────────────────────────────────────
// Password Modal — Reusable password change UI
// ─────────────────────────────────────────────────────────────
import { openModal }         from '@/ui/modal';
import { passwordStrength }  from '@/helpers';

export function openChangePasswordModal(
  email:    string,
  onSave:   (newPassword: string) => Promise<void>,
  isSelf = false
): void {
  openModal(`Alterar senha — ${email}`, `
    ${isSelf ? `<div class="fg"><label>Senha atual</label>
      <input type="password" id="f-curpass" placeholder="Senha atual" autocomplete="current-password">
    </div>` : ''}
    <div class="fg">
      <label>Nova senha</label>
      <input type="password" id="f-newpass" placeholder="Mínimo 8 caracteres"
             autocomplete="new-password" oninput="window.__checkPwdStrength(this.value)">
    </div>
    <div id="pwd-rules" style="display:flex;flex-direction:column;gap:5px;margin:-4px 0 12px;padding:10px 12px;background:var(--bg2);border-radius:var(--r)">
      <div id="r-len"   class="pwd-rule">✗ Mínimo 8 caracteres</div>
      <div id="r-upper" class="pwd-rule">✗ Uma letra maiúscula (A-Z)</div>
      <div id="r-lower" class="pwd-rule">✗ Uma letra minúscula (a-z)</div>
      <div id="r-num"   class="pwd-rule">✗ Um número (0-9)</div>
      <div id="r-spec"  class="pwd-rule">✗ Um caractere especial (!@#$…)</div>
    </div>
    <div class="fg">
      <label>Confirmar nova senha</label>
      <input type="password" id="f-newpass2" placeholder="Repita a nova senha"
             autocomplete="new-password" oninput="window.__checkPwdMatch()">
    </div>
    <div id="pwd-match" style="font-size:12px;margin-top:-8px;margin-bottom:8px;display:none"></div>`,

    async () => {
      const newPass  = (document.getElementById('f-newpass')  as HTMLInputElement)?.value ?? '';
      const newPass2 = (document.getElementById('f-newpass2') as HTMLInputElement)?.value ?? '';

      if (!passwordStrength(newPass).valid)
        throw new Error('A nova senha não atende aos requisitos de segurança.');
      if (newPass !== newPass2)
        throw new Error('As senhas não coincidem.');

      if (isSelf) {
        const curPass = (document.getElementById('f-curpass') as HTMLInputElement)?.value ?? '';
        if (!curPass) throw new Error('Informe a senha atual.');
      }

      await onSave(newPass);
    }
  );
}

// Password strength check (exposed globally for oninput)
export function checkPwdStrength(pwd: string): void {
  const s = passwordStrength(pwd);
  const set = (id: string, ok: boolean) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.textContent = (ok ? '✓ ' : '✗ ') + (el.textContent ?? '').slice(2);
    el.style.color  = ok ? 'var(--ok-tx)' : 'var(--danger-tx)';
  };
  set('r-len',   s.len);
  set('r-upper', s.upper);
  set('r-lower', s.lower);
  set('r-num',   s.num);
  set('r-spec',  s.spec);
  checkPwdMatch();
}

export function checkPwdMatch(): void {
  const p1 = (document.getElementById('f-newpass')  as HTMLInputElement)?.value ?? '';
  const p2 = (document.getElementById('f-newpass2') as HTMLInputElement)?.value ?? '';
  const el = document.getElementById('pwd-match');
  if (!el || !p2) { if (el) el.style.display = 'none'; return; }
  el.style.display = 'block';
  if (p1 === p2) {
    el.textContent = '✓ As senhas coincidem';
    el.style.color = 'var(--ok-tx)';
  } else {
    el.textContent = '✗ As senhas não coincidem';
    el.style.color = 'var(--danger-tx)';
  }
}

// Register globals
(window as unknown as Record<string, unknown>)['__checkPwdStrength'] = checkPwdStrength;
(window as unknown as Record<string, unknown>)['__checkPwdMatch']    = checkPwdMatch;
